import PageHeader from "@/components/PageHeader";
import { ShieldCheck, Warning, CheckCircle } from "@phosphor-icons/react";

const HERO =
  "https://images.unsplash.com/photo-1524168948265-8f79ad8d4e33?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJsdWUlMjBtYWlsJTIwZW52ZWxvcGUlMjAzZHxlbnwwfHx8fDE3ODc1NTI5MDd8MA&ixlib=rb-4.1.0";

const RECORDS = [
  {
    type: "SPF",
    host: "yourdomain.com  (TXT)",
    value: "v=spf1 include:_spf.yourprovider.com ~all",
    desc: "Authorises your SMTP server to send mail for your domain.",
  },
  {
    type: "DKIM",
    host: "selector._domainkey.yourdomain.com  (TXT)",
    value: "v=DKIM1; k=rsa; p=MIGfMA0GCSq...  (from your provider)",
    desc: "Cryptographically signs your emails so receivers trust they weren't altered.",
  },
  {
    type: "DMARC",
    host: "_dmarc.yourdomain.com  (TXT)",
    value: "v=DMARC1; p=quarantine; rua=mailto:dmarc@yourdomain.com",
    desc: "Tells inboxes what to do with mail that fails SPF/DKIM, and gives you reports.",
  },
];

const CHECKLIST = [
  "Use a verified custom domain in your From address (not a free @gmail sender).",
  "Publish SPF, DKIM and DMARC DNS records for that domain.",
  "Keep a real, monitored Reply-To address so replies reach you.",
  "Warm up new domains: start with low volume, grow gradually.",
  "Always include a plain-text version alongside HTML (this app does automatically).",
  "Avoid spammy words, ALL CAPS, and link shorteners in subject/body.",
  "Only send to people who expect your mail to keep bounce/complaint rates low.",
];

export default function Deliverability() {
  return (
    <div data-testid="deliverability-page">
      <PageHeader
        eyebrow="Land in the inbox"
        title="Deliverability"
        subtitle="Inboxing can't be guaranteed by any tool — it depends on your domain reputation. Set these up to maximise it."
      />

      <div className="p-10 space-y-10">
        <div className="relative border border-[#E5E7EB] overflow-hidden">
          <img src={HERO} alt="" className="w-full h-40 object-cover" />
          <div className="absolute inset-0 bg-[#09090B]/70 flex items-center px-10">
            <div className="flex items-start gap-3 max-w-2xl">
              <Warning size={22} weight="fill" color="#F59E0B" className="mt-1 shrink-0" />
              <p className="text-white text-sm leading-relaxed">
                <b>Reality check:</b> "Always inbox, never junk" is not something any sender can promise.
                The single biggest factor is authenticating a domain you own with the records below.
                Without them, mailbox providers will often route to spam.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-display font-bold text-xl tracking-tight mb-4 flex items-center gap-2">
            <ShieldCheck size={22} weight="fill" color="#2563EB" /> DNS records to publish
          </h2>
          <div className="border-t border-l border-[#E5E7EB] grid grid-cols-1">
            {RECORDS.map((r) => (
              <div key={r.type} className="border-r border-b border-[#E5E7EB] p-6" data-testid={`dns-${r.type.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="bg-[#09090B] text-white text-xs font-bold px-2.5 py-1 tracking-widest">{r.type}</span>
                  <span className="font-mono text-xs text-[#52525B]">{r.host}</span>
                </div>
                <div className="font-mono text-xs bg-[#F9FAFB] border border-[#E5E7EB] p-3 break-all mb-2">{r.value}</div>
                <p className="text-sm text-[#52525B]">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-display font-bold text-xl tracking-tight mb-4">Inboxing checklist</h2>
          <div className="border border-[#E5E7EB] bg-white">
            {CHECKLIST.map((item, i) => (
              <div key={i} className="flex items-start gap-3 px-5 py-4 border-b border-[#F3F4F6] last:border-0">
                <CheckCircle size={18} weight="fill" color="#10B981" className="mt-0.5 shrink-0" />
                <span className="text-sm text-[#09090B]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
